/* packages/apps/workoutsmart/src/App.js */
import React, { useCallback, useMemo, useEffect, useState, useRef, lazy, Suspense } from 'react';
import { Box, CircularProgress } from '@mui/material';
import CoreApp from '@platform/core';
import { AppSpecificsProvider } from '@platform/core/src/context/AppSpecificContext';
import { ApiProvider, useApi } from '@platform/core/src/context/ApiContext';
import { ResourceLoadingProvider } from '@platform/core/src/context/ResourceLoadingContext';
import { ContentTypeProvider } from '@platform/core/src/context/ContentTypeContext';
import { registerAgentIcon } from '@platform/core/src/components/agentStatus';
import { registerEffectRenderer } from '@platform/core/src/components/chat/effects/EffectRegistry';
import { LookupsProvider } from './context/LookupsContext';
import { PreferencesProvider } from './context/PreferencesContext';
import { ReferenceProvider } from './context/ReferenceContext';
import { WidgetStateProvider, useWidgetStateActions } from '@platform/core/src/context/WidgetStateContext';
import { useAppEvents } from '@platform/core/src/context/AppEventsContext';
import { useSession } from '@platform/core/src/context/SessionContext';
import { useUI } from '@platform/core/src/context/UIContext';
import { CalendarProvider } from '@platform/core/src/context/CalendarContext';
import { useTour } from '@platform/core/src/context/TourContext';
import appConfig from './appConfig';
import * as resourceApi from './services/resourceApi';
import * as profileApi from './services/profileApi';
import * as locationApi from './services/locationApi';
import * as locationFeatureApi from './services/locationFeatureApi';
import * as lookupsApi from './services/lookupsApi';
import * as exerciseDefinitionApi from './services/exerciseDefinitionApi';
import * as workoutPlanApi from './services/workoutPlanApi';
import * as workoutSessionApi from './services/workoutSessionApi';
import * as preferencesApi from './services/preferencesApi';
import * as referenceApi from '@platform/core/src/services/referenceApi';
import * as legalApi from '@platform/core/src/services/legalApi';
import * as bioLogsApi from './services/bioLogsApi';
import { getAppTheme } from './theme';
import { registerResourceSyncDriver } from '@platform/core/src/services/resourceSyncService';
import workoutPlanSyncDriver from './sync/workoutPlanSyncDriver';
import workoutSessionSyncDriver from './sync/workoutSessionSyncDriver';
import exerciseDefinitionSyncDriver from './sync/exerciseDefinitionSyncDriver';
import locationFeatureSyncDriver from './sync/locationFeatureSyncDriver';
import locationSyncDriver from './sync/locationSyncDriver';
import bioLogSyncDriver from './sync/bioLogSyncDriver';
import dateSyncDriver from './sync/dateSyncDriver';
import OnboardingCompleteMessage from './components/events/OnboardingCompleteMessage';
import ResourceSelectionMessage from './components/events/ResourceSelectionMessage';
import WorkoutSessionCompletedMessage from './components/events/WorkoutSessionCompletedMessage';
import SwitchTeamMessage from './components/events/SwitchTeamMessage';
import SessionCompletionMonitor from './components/events/SessionCompletionMonitor';
import { useOnboardingLock } from './hooks/useOnboardingRedirect';
import WorkoutAlerts from './components/alerts/WorkoutAlerts';
import WorkoutPlanEffectRenderer from './components/chat/effects/WorkoutPlanEffectRenderer';
import WorkoutSessionEffectRenderer from './components/chat/effects/WorkoutSessionEffectRenderer';
import CalendarEffectRenderer from './components/chat/effects/CalendarEffectRenderer';
import LocationEffectRenderer from './components/chat/effects/LocationEffectRenderer';
import InfoLookupEffectRenderer from './components/chat/effects/InfoLookupEffectRenderer';
import InfoProcessingEffectRenderer from './components/chat/effects/InfoProcessingEffectRenderer'; // Import here
import ActivityRequestEffectRenderer from './components/chat/effects/ActivityRequestEffectRenderer'; // Import here
import ChatResourceFetcher from './components/common/ChatResourceFetcher';
import { preloadStartupData, clearStartupCache } from './services/startupService';
import CoachWelcomeOptions from './components/chat/CoachWelcomeOptions';

const Features = lazy(() => import('./components/login/Features'));

preloadStartupData();

const appApis = {
  resource: resourceApi,
  profile: profileApi,
  location: locationApi,
  locationFeature: locationFeatureApi,
  lookups: lookupsApi,
  exerciseDefinition: exerciseDefinitionApi,
  workoutPlan: workoutPlanApi,
  workoutSession: workoutSessionApi,
  preferences: preferencesApi,
  reference: referenceApi,
  legal: legalApi,
  bioLogs: bioLogsApi,
};

const resourceLoaders = {
  fetchAllResources: resourceApi.fetchAllResources,
  processAllResources: resourceApi.processAllResources,
};

const registerAppComponents = () => {
  if (appConfig.agentIcons) {
    Object.entries(appConfig.agentIcons).forEach(([agentName, IconComponent]) => {
      registerAgentIcon(agentName, IconComponent);
    });
  }
  registerEffectRenderer('workout_plan', WorkoutPlanEffectRenderer);
  registerEffectRenderer('workout_session', WorkoutSessionEffectRenderer);
  registerEffectRenderer('date', CalendarEffectRenderer);
  registerEffectRenderer('location', LocationEffectRenderer);
  registerEffectRenderer('info_lookup', InfoLookupEffectRenderer);
  registerEffectRenderer('info_processing', InfoProcessingEffectRenderer); // Register here
  registerEffectRenderer('activity_request', ActivityRequestEffectRenderer); // Register here
};

const registerAppSyncDrivers = () => {
  registerResourceSyncDriver('workout_plan', workoutPlanSyncDriver);
  registerResourceSyncDriver('workout_session', workoutSessionSyncDriver);
  registerResourceSyncDriver('exercise_definition', exerciseDefinitionSyncDriver);
  registerResourceSyncDriver('location_feature', locationFeatureSyncDriver);
  registerResourceSyncDriver('location', locationSyncDriver);
  registerResourceSyncDriver('bio_log', bioLogSyncDriver);
  registerResourceSyncDriver('date', dateSyncDriver);
};

const BaseProviders = ({ children }) => (
  <ApiProvider apis={appApis}>
    <ResourceLoadingProvider loaders={resourceLoaders}>
      <ContentTypeProvider contentTypes={appConfig.contentTypes}>
        <LookupsProvider>
          {children}
        </LookupsProvider>
      </ContentTypeProvider>
    </ResourceLoadingProvider>
  </ApiProvider>
);

const WorkoutSmartAppSpecificsProvider = ({ children }) => {
    const providerValue = useMemo(() => ({
        filterResourcesForDisplay: (resources) => resources,
        getResourceListOrganizer: () => null,
        canResourceBeChildOf: (child, parent) => {
            const parentType = parent?.type || parent?.collection_type;
            return parentType === 'collection' || parentType === 'unit';
        },
    }), []);

    return (
        <AppSpecificsProvider value={providerValue}>
            {children}
        </AppSpecificsProvider>
    );
};

const InnerProviders = ({ children }) => (
  <WorkoutSmartAppSpecificsProvider>
    <PreferencesProvider>
      <ReferenceProvider>
        <CalendarProvider>
          <WidgetStateProvider>
            {children}
          </WidgetStateProvider>
        </CalendarProvider>
      </ReferenceProvider>
    </PreferencesProvider>
  </WorkoutSmartAppSpecificsProvider>
);

const WorkoutSmartEventHandlers = () => {
  const { registerAppEventHandler, unregisterAppEventHandler } = useAppEvents();
  const { addTimelineItem, sessionId } = useSession();
  const { closeAllResourceTabs } = useUI();
  const { showSpotlight } = useTour();

  useEffect(() => {
    const onboardingCompleteHandler = (event) => {
      if (event.session_id !== sessionId) {
        return;
      }
      clearStartupCache();
      addTimelineItem({
        source: 'APP_EVENT',
        type: 'message',
        content: <OnboardingCompleteMessage />,
        timestamp: new Date().toISOString(),
        scrollMode: 'top',
      });
    };

    const resourceSelectionHandler = (event) => {
      if (event.session_id !== sessionId) {
        return;
      }
      const { payload } = event.content;
      if (payload && payload.requests) {
        addTimelineItem({
          source: 'APP_EVENT',
          type: 'message',
          content: <ResourceSelectionMessage requests={payload.requests} eventId={payload.event_id} />,
          timestamp: new Date().toISOString(),
        });
      } else {
        console.warn("Received REQUEST_RESOURCE_SELECTIONS event with invalid payload:", event);
      }
    };

    const workoutSessionCompletedHandler = (event) => {
      if (event.session_id !== sessionId) {
        return;
      }
      addTimelineItem({
        source: 'APP_EVENT',
        type: 'message',
        content: <WorkoutSessionCompletedMessage />,
        timestamp: new Date().toISOString(),
      });
    };

    const handleSwitchTeam = (event, context) => {
       console.log("WorkoutSmart App: Intercepted switch_team event.");
       const payload = event.content?.payload;
       const targetTeam = payload?.team_name;
       if (!targetTeam) {
         console.warn("switch_team event missing team_name");
         return false;
       }
       
       addTimelineItem({
         source: 'APP_EVENT',
         type: 'message',
         content: <SwitchTeamMessage targetTeam={targetTeam} payload={payload} />,
         timestamp: new Date().toISOString(),
       });
       return true;
    };

    const handleShowUiHint = (event) => {
        const payload = event.content?.payload;
        if (payload && payload.targetId && payload.title && payload.content) {
            console.log("Triggering UI Hint:", payload);
            showSpotlight({
                targetId: payload.targetId,
                title: payload.title,
                content: payload.content,
                placement: payload.placement || 'bottom'
            });
        }
    };

    registerAppEventHandler('switch_team', handleSwitchTeam);
    registerAppEventHandler('onboarding_complete', onboardingCompleteHandler);
    registerAppEventHandler('REQUEST_RESOURCE_SELECTIONS', resourceSelectionHandler);
    registerAppEventHandler('workout_session_completed', workoutSessionCompletedHandler);
    registerAppEventHandler('SHOW_UI_HINT', handleShowUiHint);

    return () => {
      unregisterAppEventHandler('switch_team');
      unregisterAppEventHandler('onboarding_complete');
      unregisterAppEventHandler('REQUEST_RESOURCE_SELECTIONS', resourceSelectionHandler);
      unregisterAppEventHandler('workout_session_completed', workoutSessionCompletedHandler);
      unregisterAppEventHandler('SHOW_UI_HINT');
    };
  }, [registerAppEventHandler, unregisterAppEventHandler, addTimelineItem, sessionId, closeAllResourceTabs, showSpotlight]);

  return null;
};

const determineSessionStartup = async (user) => {
    return {
        team_name: appConfig.defaultTeamName || 'welcome_group'
    };
};

const AppLogic = () => {
  useOnboardingLock();
  const { startTour, isOpen } = useTour();
  const { teamName, preChatState, timelineItems, addTimelineItem, sessionId } = useSession();
  const { uiState } = useApi();
  const { clearAllStates } = useWidgetStateActions();
  const [tourCompleted, setTourCompleted] = useState(true);
  const [hasCheckedStatus, setHasCheckedStatus] = useState(false);
  const tourStartedRef = useRef(false);
  const prevIsOpen = useRef(isOpen);
  const welcomeOptionsInjected = useRef(false);

  useEffect(() => {
    clearAllStates();
  }, [sessionId, clearAllStates]);

  useEffect(() => {
    let mounted = true;
    const checkTourStatus = async () => {
      try {
        const state = await uiState.getUiState();
        if (mounted) {
          console.log("[AppLogic] UI State fetched:", state);
          setTourCompleted(!!state.tour_completed);
        }
      } catch (err) {
        console.warn("Failed to fetch UI state for tour:", err);
      } finally {
        if (mounted) setHasCheckedStatus(true);
      }
    };
    checkTourStatus();
  }, [uiState]);

  useEffect(() => {
    if (teamName === 'onboarding_team' || preChatState) {
      tourStartedRef.current = false;
    }
  }, [teamName, preChatState]);

  useEffect(() => {
    if (!hasCheckedStatus) return;
    if (tourCompleted) return;
    if (tourStartedRef.current) return;

    const isSurveyActive = !!preChatState;
    const isOnboardingTeam = teamName === 'onboarding_team';
    const isValidTeam = teamName && !isOnboardingTeam;

    if (isValidTeam && !isSurveyActive && appConfig.onboardingTour) {
      tourStartedRef.current = true;
      console.log("[AppLogic] User is not in onboarding/survey and has not seen tour. Starting tour.");
      const timer = setTimeout(() => {
        startTour(appConfig.onboardingTour.steps);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [hasCheckedStatus, tourCompleted, teamName, preChatState, startTour]);

  useEffect(() => {
    if (prevIsOpen.current === true && isOpen === false && tourStartedRef.current) {
        console.log("[AppLogic] Tour closed. Persisting 'tour_completed' state.");
        setTourCompleted(true);
        uiState.updateUiState({ tour_completed: true }).catch(err => {
            console.error("Failed to persist tour completion:", err);
        });
    }
    prevIsOpen.current = isOpen;
  }, [isOpen, uiState]);

  useEffect(() => {
    welcomeOptionsInjected.current = false;
  }, [sessionId]);

  useEffect(() => {
    const isDefaultTeam = !teamName || teamName === 'welcome_group';
    if (isDefaultTeam && !welcomeOptionsInjected.current && !preChatState) {
       welcomeOptionsInjected.current = true;
       addTimelineItem({
           id: 'welcome-options',
           type: 'message',
           source: 'APP_EVENT',
           content: <CoachWelcomeOptions />,
           timestamp: new Date().toISOString(),
           variant: 'card'
       });
    }
  }, [teamName, addTimelineItem, preChatState]);

  return (
    <>
      <WorkoutSmartEventHandlers />
      <SessionCompletionMonitor />
      <ChatResourceFetcher />
      <WorkoutAlerts />
    </>
  );
};

const sseEventFilter = (event) => {
  if (event.type === 'message' && typeof event.content === 'string' && event.source === 'User') {
    if (event.content.trim().startsWith('[RESPONSE]') && event.content.trim().endsWith('[/RESPONSE]')) {
      return false;
    }
  }
  return true;
};

function WorkoutSmartApp({ buildInfo }) {
  const handleAppResourceSync = useCallback((resourceTypesChanged) => {
    const domainTypes = ['workout_session', 'workout_plan', 'location', 'exercise_definition', 'equipment', 'bio_log'];
    const hasDomainTypes = [...resourceTypesChanged].some(type => domainTypes.includes(type));
    if (hasDomainTypes) {
        console.log("[WorkoutSmartApp] App-level sync handler: Domain resource update detected. Handled via local state injection (SessionContext). No refresh needed.");
    } else {
        console.log("[WorkoutSmartApp] App-level sync handler called for types:", resourceTypesChanged);
    }
  }, []);

  return (
    <BaseProviders>
      <CoreApp
        registerAppComponents={registerAppComponents}
        registerSyncDrivers={registerAppSyncDrivers}
        InnerAppProviders={InnerProviders}
        onAppResourceSync={handleAppResourceSync}
        eventHandlerInitializer={<AppLogic />}
        config={appConfig}
        loginPageFeatures={
          <Suspense fallback={
            <Box sx={{ py: 10, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress color="inherit" size={30} />
            </Box>
          }>
            <Features />
          </Suspense>
        }
        sseEventFilter={sseEventFilter}
        getAppTheme={getAppTheme}
        sessionInitStrategy={determineSessionStartup}
        buildInfo={buildInfo}
      />
    </BaseProviders>
  );
}

export default WorkoutSmartApp;